import React, { useState, useEffect, useCallback } from 'react';
import { NavItem, NavLink, Nav, TabContent } from 'reactstrap';
import i18n from '../../views/Pages/Login/i18n';
import classnames from 'classnames';
import { withRouter, matchPath } from 'react-router';
import { withApollo } from 'react-apollo';
import gql from 'graphql-tag';

import { checkPathForTrue } from '../../utils/Helpers';

const NavTabs = (props) => {
  const { items, tabKey: propTabKey, location, children, displayNone, queries, client } = props;
  const tabKey = 'tab_' + (propTabKey || 'default');

  // State to store which items should be shown
  const [visibleItems, setVisibleItems] = useState([]);

  // Find active tab from location or sessionStorage
  const getActiveTab = useCallback(() => {
    const tabPos = location.pathname.indexOf(tabKey);
    if (tabPos > -1) {
      const endPos = location.pathname.indexOf('/', tabPos);
      const param = location.pathname.substring(tabPos + tabKey.length + 1, endPos === -1 ? undefined : endPos);
      const linkedItem = Array.isArray(items)
        ? items.find((item) => item.title === param)
        : Object.values(items).find((item) => item.title === param);
      if (linkedItem && linkedItem.key) {
        return linkedItem.key;
      }
    }
    return null;
  }, [location.pathname, tabKey, items]);

  // Set tab from hash path if matched
  useEffect(() => {
    for (const key in items) {
      const item = items[key];
      if (item.path) {
        const match = matchPath(window.location.hash.substr(1), {
          path: '/view/:name' + item.path,
          exact: false,
          strict: false,
        });
        if (match) {
          sessionStorage[tabKey] = item.key;
        }
      }
    }
  }, [items, tabKey]);

  const [activeTab, setActiveTab] = useState(() => getActiveTab() || sessionStorage[tabKey] || '1');

  // Keep activeTab in sync with location changes
  useEffect(() => {
    const tab = getActiveTab();
    if (tab && tab !== activeTab) {
      setActiveTab(tab);
    }
    // eslint-disable-next-line
  }, [location.pathname, getActiveTab]);

  useEffect(() => {
    if (visibleItems.length > 0 && !visibleItems.some((item) => item.key === activeTab)) {
      const firstVisibleKey = visibleItems[0].key;
      setActiveTab(firstVisibleKey);
      sessionStorage[tabKey] = firstVisibleKey;
    }
    // Only run when visibleItems or activeTab changes
    // eslint-disable-next-line
  }, [visibleItems, activeTab, tabKey]);

  const toggle = (tab) => {
    if (activeTab !== tab) {
      sessionStorage[tabKey] = tab;
      setActiveTab(tab);

      if (window.gtag) {
        const filtered = (Array.isArray(items) ? items : Object.values(items)).filter((i) => i.key === tab);
        const title = filtered.length > 0 ? filtered[0].title : undefined;
        const path = window.location.hash.substr(1) + '/tab_' + title;
        window.gtag('config', window.config.consul.tracking['ga-code'], {
          page_title: title,
          page_path: path,
        });
      }
    }
  };

  useEffect(() => {
    let isMounted = true;

    /**
     * Asynchronously filters and sets visible navigation items based on specified conditions.
     *
     * For each item in the `items` array, if the item has visibility conditions, it performs a GraphQL query
     * to determine if the item should be visible. If the conditions are met (as determined by `checkPathForTrue`),
     * the item is included in the visible items list. Otherwise, it is excluded.
     *
     * After processing all items, updates the visible items state if the component is still mounted.
     *
     * @async
     * @function fetchVisibleItems
     * @returns {Promise<void>} Resolves when visible items have been determined and state updated.
     */
    const fetchVisibleItems = async () => {
      const results = await Promise.all(
        items.map(async (item) => {
          if (item.conditions && Object.keys(item.conditions).length > 0) {
            // Check conditions for visibility
            let { property, id, queryKey, path } = item.conditions;
            try {
              const query = queries[queryKey];
              const response = await client.query({
                query: gql`
                  ${query}
                `,
                variables: {
                  size: 1000,
                  page: 0,
                  where: `${property} == ${id}`,
                },
              });
              if (checkPathForTrue(response.data, path)) {
                return item;
              }
              return null;
            } catch (error) {
              console.error(error);
              return null;
            }
          }
          return item;
        })
      );
      if (isMounted) {
        setVisibleItems(results.filter(Boolean));
      }
    };
    fetchVisibleItems();
    return () => {
      isMounted = false;
    };
  }, [items, queries, client]);

  // Don't render until visibleItems are loaded and activeTab is valid
  if (visibleItems.length === 0 || !visibleItems.some((item) => item.key === activeTab)) {
    return null; // or a loading spinner if you prefer
  }

  // Render nav items
  const navItems = [];
  for (const item of visibleItems) {
    navItems.push(
      <NavItem key={item.key}>
        <NavLink className={classnames({ active: activeTab === item.key })} onClick={() => toggle(item.key)}>
          {i18n.t(item.title)}
        </NavLink>
      </NavItem>
    );
  }

  // Find the correct child to display
  let childToDisplay = null;
  if (Array.isArray(children)) {
    const activeChildrenIndex = children.findIndex((child, i) => {
      if (i % 2 !== 0) return child.props.tabId === activeTab;
      return false;
    });
    childToDisplay = children.slice(activeChildrenIndex - 1, activeChildrenIndex + 1);
  } else {
    childToDisplay = children;
  }

  return (
    <div style={{ width: '100%' }}>
      <div className='bp-nav-items' style={{ display: displayNone ? 'none' : 'block' }}>
        <Nav tabs>{navItems}</Nav>
      </div>
      <TabContent activeTab={activeTab} className='bp-tab-content'>
        {childToDisplay}
      </TabContent>
    </div>
  );
};

export default withApollo(withRouter(NavTabs));
